import React, { useState } from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import BoardMeetingTenderRecap from './BoardMeetingTenderRecap';
import BoardContractsRecap from './BoardContractsRecap';
import BoardResolutionsGrid, { buildResolutions } from './BoardResolutionsGrid';
import DividendSplitPanel from './DividendSplitPanel';
import FleetReportBlock from './FleetReportBlock';
import AnnualHRReport from './AnnualHRReport';
import AnnualYearDebrief from './AnnualYearDebrief';
import {
  Briefcase, TrendingUp, TrendingDown, Award, Users, Truck,
  Star, BarChart3, ArrowUpRight, ArrowDownRight, X, ThumbsUp, Calendar,
} from 'lucide-react';

const MONTHS_FR = ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

/**
 * Conseil d'administration — fin d'exercice annuel.
 *
 * 04/06/2026 refactor : extracted `BoardResolutionsGrid`, `AnnualHRReport`
 * and the chairman/monthly-chart helpers below to keep this orchestrator
 * file under ~250 lines. Zero behaviour change.
 */
const AnnualBoardMeeting = () => {
  const { pendingAnnualReport, applyBoardResolution, gameStarted, company, drivers, treasury, vehicles, personalWealth } = useGameStore();
  const [chosen, setChosen] = useState(null);
  // 22/07/2026 — répartition libre du dividende (curseurs).
  const [divSplit, setDivSplit] = useState({ bonus: 0, personal: 0 });
  // 25/07/2026 — onglet « Débrief de l'année » (demande user).
  const [view, setView] = useState('bilan');

  if (!gameStarted || !pendingAnnualReport) return null;

  const r = pendingAnnualReport;
  const isProfit = r.netResult >= 0;
  const margin = r.totalRevenue > 0 ? Math.round((r.netResult / r.totalRevenue) * 100) : 0;
  const treasuryUp = r.treasuryDelta >= 0;
  const driversCount = (drivers || []).length;
  // Estimations pour la projection d'économies du plan d'investissement.
  const maintenanceMonthly = (vehicles || []).reduce((s, v) => s + (v.maintenancePerMonth || 0), 0);
  const fuelMonthly = (vehicles || []).reduce((s, v) => s + (v.dailyFuel || 0), 0) * 22;
  const resolutions = buildResolutions(r, treasury, driversCount, { maintenanceMonthly, fuelMonthly });
  const divResolution = resolutions.find(x => x.id === 'dividend');
  const divTotal = divSplit.bonus + divSplit.personal;
  const chairmanLine = pickChairmanLine(margin);

  const handleConfirm = () => {
    if (!chosen) return;
    if (chosen === 'dividend') {
      if (divTotal <= 0) return;
      applyBoardResolution('dividend', { bonusAmount: divSplit.bonus, personalAmount: divSplit.personal });
      return;
    }
    applyBoardResolution(chosen);
  };

  return (
    <div
      className="fixed inset-0 z-[88] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="annual-board-meeting"
    >
      <div className="bg-ink-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col">
        {/* Header — board-room green leather feel */}
        <div className="bg-gradient-to-r from-[#0b3d2e] via-[#0e5a3f] to-[#0b3d2e] px-6 py-5 flex items-center justify-between border-b border-emerald-900/40">
          <div className="flex items-center gap-3 text-white">
            <div className="p-2 rounded-lg bg-white/10">
              <Briefcase className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-200/80 font-mono">Conseil d&apos;administration</p>
              <h3 className="font-bold text-xl">Bilan de l&apos;exercice {r.year}</h3>
              <p className="text-xs text-emerald-100/70">{company?.name || 'HST'} — Clôture annuelle</p>
            </div>
          </div>
          <button
            onClick={() => applyBoardResolution('statu_quo')}
            className="text-white/60 hover:text-white transition-colors p-1"
            data-testid="close-annual-report"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 25/07/2026 — Onglets Bilan / Débrief de l'année */}
        {r.yearDebrief && (
          <div className="px-6 pt-3 flex gap-2 border-b border-ink-600/60">
            {[
              { id: 'bilan', label: 'Bilan & résolutions' },
              { id: 'debrief', label: `Débrief de l'année ${r.year}` },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                data-testid={`board-tab-${tab.id}`}
                className={`px-3 py-1.5 rounded-t-lg text-xs font-bold transition-colors ${
                  view === tab.id
                    ? 'bg-ink-700/60 text-emerald-300 border border-b-0 border-ink-600'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Scrollable body */}
        <div className="p-6 space-y-5 overflow-y-auto">
          {view === 'debrief' && r.yearDebrief ? (
            <AnnualYearDebrief debrief={r.yearDebrief} companyName={company?.name} />
          ) : (
            <>
          {/* Chairman's address */}
          <div className="bg-emerald-900/15 border-l-4 border-emerald-500 rounded-r-lg px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-mono">Mot du Président</p>
            <p className="text-sm text-slate-200 italic mt-1" data-testid="chairman-address">« {chairmanLine} »</p>
          </div>

          {/* Top P&L cards */}
          <div className="grid grid-cols-3 gap-3">
            <PnLCard
              icon={TrendingUp}
              label="CA annuel"
              value={formatMoney(r.totalRevenue)}
              tone="money"
              testId="annual-revenue"
            />
            <PnLCard
              icon={TrendingDown}
              label="Charges"
              value={formatMoney(r.totalExpenses)}
              tone="danger"
              testId="annual-expenses"
            />
            <div className={`${isProfit ? 'bg-money/15 border-money/30' : 'bg-danger/15 border-danger/30'} border rounded-xl p-3 text-center`}>
              <BarChart3 className={`w-5 h-5 mx-auto mb-1 ${isProfit ? 'text-money' : 'text-danger'}`} />
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Résultat net</p>
              <p className={`text-lg font-bold font-mono ${isProfit ? 'text-money' : 'text-danger'}`} data-testid="annual-netresult">
                {isProfit ? '+' : ''}{formatMoney(r.netResult)}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">Marge {margin >= 0 ? '+' : ''}{margin}%</p>
            </div>
          </div>

          {/* 12-month bar chart */}
          <MonthlyBarChart months={r.months} />

          {/* Best / Worst months */}
          <div className="grid grid-cols-2 gap-3">
            <BestWorstMonthCard
              tone="money"
              icon={Award}
              label="Meilleur mois"
              month={r.bestMonth}
              year={r.year}
              testId="annual-best"
              prefix={r.bestMonth.netResult >= 0 ? '+' : ''}
            />
            <BestWorstMonthCard
              tone="danger"
              icon={ArrowDownRight}
              label="Mois le plus dur"
              month={r.worstMonth}
              year={r.year}
              testId="annual-worst"
              prefix={r.worstMonth.netResult >= 0 ? '+' : ''}
            />
          </div>

          {/* Treasury delta + key indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              {treasuryUp
                ? <ArrowUpRight className="w-4 h-4 text-money mx-auto mb-0.5" />
                : <ArrowDownRight className="w-4 h-4 text-danger mx-auto mb-0.5" />}
              <p className="text-[10px] text-slate-500">Variation trésorerie</p>
              <p className={`text-xs font-bold font-mono ${treasuryUp ? 'text-money' : 'text-danger'}`} data-testid="annual-treasury-delta">
                {treasuryUp ? '+' : ''}{formatMoney(r.treasuryDelta)}
              </p>
            </div>
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              <Truck className="w-4 h-4 text-slate-300 mx-auto mb-0.5" />
              <p className="text-[10px] text-slate-500">Véhicules</p>
              <p className="text-xs font-bold text-slate-200">{r.vehiclesTotal}</p>
            </div>
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              <Users className="w-4 h-4 text-slate-300 mx-auto mb-0.5" />
              <p className="text-[10px] text-slate-500">Chauffeurs</p>
              <p className="text-xs font-bold text-slate-200">{r.driversTotal}</p>
            </div>
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              <Star className="w-4 h-4 text-warn mx-auto mb-0.5" />
              <p className="text-[10px] text-slate-500">Réputation / Notoriété</p>
              <p className="text-xs font-bold text-slate-200">{r.reputation} / {r.notoriety}</p>
            </div>
          </div>

          {/* Treasury endpoints */}
          <div className="flex items-center justify-between bg-ink-700/30 rounded-lg px-3 py-2 text-xs">
            <div>
              <p className="text-[10px] text-slate-500">Trésorerie début exercice</p>
              <p className="font-bold font-mono text-slate-200">{formatMoney(r.treasuryStart)}</p>
            </div>
            <Calendar className="w-4 h-4 text-slate-500" />
            <div className="text-right">
              <p className="text-[10px] text-slate-500">Trésorerie clôture</p>
              <p className="font-bold font-mono text-slate-200">{formatMoney(r.treasuryEnd)}</p>
            </div>
          </div>

          {/* Évaluation annuelle des chauffeurs */}
          {(r.topDrivers && r.topDrivers.length > 0) && (
            <DriverEvaluation topDrivers={r.topDrivers} bottomDrivers={r.bottomDrivers} />
          )}

          {/* 22/07/2026 — Rapport Flotte annuel (Rémi Forni) */}
          <FleetReportBlock report={r.fleetReport} />

          {/* Bilan RH */}
          <AnnualHRReport rhReport={r.rhReport} avoidableHrCost={r.avoidableHrCost} />

          {/* Appels d'Offres recap (yearly) */}
          <BoardMeetingTenderRecap stats={r.tenderStats} scope="année" />

          {/* 22/07/2026 — Contrats honorés vs laissés filer par tranche */}
          <BoardContractsRecap stats={r.contractDeals} />

          {/* Vote du conseil */}
          <BoardResolutionsGrid
            resolutions={resolutions}
            chosen={chosen}
            onChoose={setChosen}
          />
          {chosen === 'dividend' && divResolution?.enabled && (
            <DividendSplitPanel
              max={divResolution.maxDividend || 0}
              bonus={divSplit.bonus}
              personal={divSplit.personal}
              onChange={setDivSplit}
              driversCount={driversCount}
              personalWealth={personalWealth || 0}
            />
          )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-ink-600 bg-ink-700/30 flex items-center justify-between flex-wrap gap-2">
          <p className="text-[11px] text-slate-500 italic">
            {chosen === 'dividend' && divTotal <= 0
              ? 'Réglez les curseurs de répartition du dividende pour pouvoir voter.'
              : chosen
                ? `Résolution sélectionnée. Cliquez sur "Adopter la résolution" pour la voter.`
                : "Vous pouvez aussi passer en statu quo (X) — aucune résolution adoptée."}
          </p>
          <button
            onClick={handleConfirm}
            disabled={!chosen || (chosen === 'dividend' && divTotal <= 0)}
            className="bg-emerald-600 text-white py-2 px-5 rounded-lg text-sm font-bold hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="confirm-resolution-btn"
          >
            <ThumbsUp className="w-4 h-4" /> Adopter la résolution
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Sub-components (kept in this file: small + cohesive with the modal) ─ */

function PnLCard({ icon: Icon, label, value, tone, testId }) {
  const bg = tone === 'money' ? 'bg-money/10 border-money/20 text-money' : 'bg-danger/10 border-danger/20 text-danger';
  return (
    <div className={`${bg} border rounded-xl p-3 text-center`}>
      <Icon className={`w-5 h-5 mx-auto mb-1 ${tone === 'money' ? 'text-money' : 'text-danger'}`} />
      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold font-mono ${tone === 'money' ? 'text-money' : 'text-danger'}`} data-testid={testId}>{value}</p>
    </div>
  );
}

function BestWorstMonthCard({ tone, icon: Icon, label, month, year, testId, prefix }) {
  const isMoney = tone === 'money';
  return (
    <div className={`${isMoney ? 'bg-money/5 border-money/20' : 'bg-danger/5 border-danger/20'} border rounded-xl p-3`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${isMoney ? 'text-money' : 'text-danger'}`} />
        <p className={`text-[10px] uppercase tracking-wider font-mono ${isMoney ? 'text-money' : 'text-danger'}`}>{label}</p>
      </div>
      <p className="text-sm font-bold text-slate-200">{MONTHS_FR[month.month]} {year}</p>
      <p className={`text-xs font-mono ${isMoney ? 'text-money' : 'text-danger'}`} data-testid={testId}>{prefix}{formatMoney(month.netResult)}</p>
    </div>
  );
}

function MonthlyBarChart({ months }) {
  const monthsMap = new Map(months.map(m => [m.month, m]));
  const monthsAll = Array.from({ length: 12 }, (_, i) => {
    const monthNum = i + 1;
    return monthsMap.get(monthNum) || { month: monthNum, netResult: 0, totalRevenue: 0, totalExpenses: 0 };
  });
  const maxAbs = Math.max(...monthsAll.map(m => Math.abs(m.netResult || 0)), 1);

  return (
    <div className="bg-ink-700/30 rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-mono mb-3">Évolution mensuelle (Résultat net)</p>
      <div className="grid grid-cols-12 gap-1 items-end h-28" data-testid="annual-monthly-chart">
        {monthsAll.map(m => {
          const h = Math.round((Math.abs(m.netResult || 0) / maxAbs) * 100);
          const positive = (m.netResult || 0) >= 0;
          return (
            <div key={m.month} className="flex flex-col items-center justify-end gap-1 h-full">
              <div
                className={`w-full rounded-sm transition-all ${positive ? 'bg-money/80' : 'bg-danger/80'}`}
                style={{ height: `${Math.max(2, h)}%` }}
                title={`${MONTHS_FR[m.month]} : ${(m.netResult || 0) >= 0 ? '+' : ''}${(m.netResult || 0).toLocaleString('fr-FR')} €`}
                data-testid={`annual-month-bar-${m.month}`}
              />
              <span className="text-[9px] text-slate-500 font-mono">{MONTHS_FR[m.month]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DriverEvaluation({ topDrivers, bottomDrivers }) {
  return (
    <div className="border-t border-ink-600 pt-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 rounded bg-emerald-900/30">
          <Users className="w-4 h-4 text-emerald-400" />
        </div>
        <div>
          <h4 className="font-bold text-sm text-slate-200">Évaluation des chauffeurs</h4>
          <p className="text-[10px] text-slate-500">Le conseil reconnaît les meilleurs (+5 satisfaction auto) et identifie les profils à former</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-money/5 border border-money/20 rounded-lg p-3" data-testid="annual-top-drivers">
          <p className="text-[10px] uppercase tracking-wider text-money font-mono mb-2 flex items-center gap-1">
            <Award className="w-3 h-3" /> Top 3 chauffeurs
          </p>
          <ul className="space-y-1.5">
            {topDrivers.map((d, i) => (
              <li key={d.id} className="text-xs" data-testid={`top-driver-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-200">#{i + 1} {d.name}</span>
                  <span className="text-[10px] text-money font-mono">CA {formatMoney(d.yearRevenue || 0)}</span>
                </div>
                <p className="text-[10px] text-slate-500 font-mono">
                  Niv.{d.level} · Comp. {d.competence}/5 · {d.yearTrips || 0} trajet{(d.yearTrips || 0) > 1 ? 's' : ''} cette année
                  {(d.yearMissedDays || 0) > 0 ? ` · ${d.yearMissedDays} j manqué${d.yearMissedDays > 1 ? 's' : ''}` : ''} · Sat. {Math.round(d.satisfaction)}/100
                </p>
              </li>
            ))}
          </ul>
        </div>
        {bottomDrivers && bottomDrivers.length > 0 && (
          <div className="bg-warn/5 border border-warn/20 rounded-lg p-3" data-testid="annual-bottom-drivers">
            <p className="text-[10px] uppercase tracking-wider text-warn font-mono mb-2 flex items-center gap-1">
              <ArrowDownRight className="w-3 h-3" /> À former / suivre
            </p>
            <ul className="space-y-1.5">
              {bottomDrivers.map((d, i) => (
                <li key={d.id} className="text-xs" data-testid={`bottom-driver-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-200">{d.name}</span>
                    <span className="text-[10px] text-slate-400 font-mono">CA {formatMoney(d.yearRevenue || 0)}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 font-mono">
                    Niv.{d.level} · Comp. {d.competence}/5 · {d.yearTrips || 0} trajet{(d.yearTrips || 0) > 1 ? 's' : ''} cette année
                    {(d.yearMissedDays || 0) > 0 ? ` · ${d.yearMissedDays} j manqué${d.yearMissedDays > 1 ? 's' : ''}` : ''} · Sat. {Math.round(d.satisfaction)}/100
                  </p>
                </li>
              ))}
            </ul>
            <p className="text-[9px] text-slate-500 italic mt-2">Pensez à les inscrire en formation depuis la page RH.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Pure helper : pick the chairman's address based on margin ──────── */
function pickChairmanLine(margin) {
  if (margin >= 25) return 'Exercice exceptionnel. Le conseil félicite la direction et envisage un dividende.';
  if (margin >= 10) return 'Année solide. Les marges progressent — poursuivons la stratégie.';
  if (margin >= 0)  return 'Équilibre maintenu. Restons vigilants sur les charges.';
  if (margin >= -10) return 'Année difficile. Le conseil demande un plan de redressement.';
  return 'Pertes importantes. Le conseil exige des mesures fortes pour l\'exercice suivant.';
}

export default AnnualBoardMeeting;
